import { twMerge } from "tailwind-merge";

// Interfaces
import { createFilterOptions, FilterOptionsState } from "@mui/material";
import { IOption, ISmartBasicsProps, TSelectValue } from "./interfaces";

// Utils
const filterOptions = createFilterOptions();

const inputShouldHide = (blurred = false, multiple = false, value?: object[]): boolean => {
	const hasValues = multiple && value?.length;
	return Boolean(blurred && hasValues);
};

const isCompatibleOption = (option: object, value: object, selectBy = "_id"): boolean => {
	const { [selectBy as keyof object]: optionKey } = option || {};
	const { [selectBy as keyof object]: valueKey } = value || {};
	return `${optionKey}` === `${valueKey}`;
};

const isAllDisabled = (disabled = false, loading = false): boolean => disabled || loading;
const isAllSelectable = (multiple = false, multipleSelectAll = false) => multiple && multipleSelectAll;
const isAllChecked = (options: unknown[] = [], selected: unknown[] = []) => options?.length === selected?.length;
const isAllSelected = (allSelectable = false, allChecked = false) => allSelectable && allChecked;
const isHiddenInput = ({ isBlurred = false, multiple = false, tagsVariant = "", value = [] as object[] }) =>
	inputShouldHide(isBlurred, multiple, value) && tagsVariant === "text";

const isAddingOption = (value: TSelectValue = null, addable = false) => {
	if (!addable) {
		return false;
	}
	if (Array.isArray(value) && (value as object[])?.length) {
		return value.some((option) => option?.["addedOption" as keyof object]);
	}
	return Boolean(value?.["addedOption" as keyof object]);
};

const getPlaceholder = (placeholder = "", args: (ISmartBasicsProps & { value: TSelectValue }) | null = null) => {
	const { value, multiple = false, open = false } = args ?? {};

	if (!placeholder) {
		return "";
	}

	const isMultipleFilled = multiple && Array.isArray(value) && (value as object[])?.length;
	if (isMultipleFilled && !open) {
		return "";
	}

	return placeholder;
};

const getSelectedOption = (options: object[], value: string, selectBy = "_id"): TSelectValue => {
	return options.find((option) => option[selectBy as keyof object] === value) ?? null;
};

const getOptionLabel = (option: IOption, args: ISmartBasicsProps): string => {
	const { showBy = "label" } = args;
	const { [showBy as keyof object]: title } = option || {};
	return `${title}`;
};

const getTextTags = (tags: object[] = [], args: ISmartBasicsProps = {}) => {
	const { selectBy = "_id", showBy = "label" } = args || {};
	const labels = tags.map((option) => getOptionLabel(option, { selectBy, showBy }));
	return labels.join(", ");
};

const getFilteredOptions = (options: object[] | IOption[], state: FilterOptionsState<object>, args: ISmartBasicsProps): object[] => {
	const { selectBy = "_id", showBy = "label", addable = false } = args || {};
	const filtered: object[] = filterOptions(options, state as FilterOptionsState<unknown>) as object[];

	if (!addable) {
		return filtered;
	}

	const { inputValue } = state as object & { inputValue: string };
	const existSomeEqualOption = filtered.some(
		(option: IOption) => (option[showBy as keyof IOption] as string).toLowerCase() === inputValue.toLowerCase()
	);
	if (inputValue && !existSomeEqualOption) {
		filtered.push({
			[selectBy]: inputValue.replaceAll(" ", "_").toLowerCase(),
			[showBy]: `Add "${inputValue}"`,
			addedOption: inputValue,
		});
	}

	return filtered;
};

const getAppendingOptions = (options: object[] = [], args: ISmartBasicsProps & { value?: TSelectValue } = {}): object[] => {
	const { appendOptions = false, multiple = false, selectBy = "_id", value } = args || {};
	if (!appendOptions || !value) {
		return options;
	}
	const data = [...options];
	const selectedOptions = multiple ? (value as object[]) : [value as object];
	const existingIds = data.map((option) => option[selectBy as keyof object]);
	const newOptions = selectedOptions.filter((option) => !existingIds.includes(option[selectBy as keyof object]));
	return [...newOptions, ...data];
};

const getPaperProps = (args?: object & { sx?: object; className?: string; avoidShadow?: boolean }): object => {
	const { sx = undefined, className = "", avoidShadow = false, ...props } = args ?? {};
	const shadowClass = avoidShadow ? "!shadow-none" : "!shadow-selector-paper-shadow";

	return {
		className: twMerge("[&_.MuiAutocomplete-noOptions]:!font-sans", shadowClass, className),
		sx: { ...sx },
		...props,
	};
};

const getPopperProps = (args?: object & { sx?: object; className?: string }): object => {
	const { sx = undefined, className = "", ...props } = args ?? {};
	return {
		placement: "bottom-start",
		className: twMerge("!z-[998]", className),
		sx: { ...sx },
		...props,
	};
};

const getMappedSelection = (selection: TSelectValue, args: ISmartBasicsProps): TSelectValue => {
	const { selectBy = "_id", showBy = "label", multiple = false } = args || {};
	const modifyOption = (option: TSelectValue, label: string | undefined) => {
		if (!label) {
			return option;
		}
		return {
			...option,
			[showBy as keyof object]: label,
			[selectBy as keyof object]: label,
		};
	};

	if (multiple && Array.isArray(selection)) {
		return selection.map((option) => {
			const addedOption = option?.["addedOption" as keyof object];
			return modifyOption(option, addedOption);
		});
	}

	const added = selection?.["addedOption" as keyof object];
	return modifyOption(selection, added);
};

const getSortedOptions = <T extends {} = object>(options: T[] = [], sortBy = "", sortMethod = "ASC"): T[] => {
	if (!sortBy) {
		return options;
	}

	const sortedData = [...options];
	if (sortMethod === "DESC") {
		sortedData.sort((a, b) => (b[sortBy as keyof T] > a[sortBy as keyof T] ? 1 : -1));
	} else {
		sortedData.sort((a, b) => (a[sortBy as keyof T] > b[sortBy as keyof T] ? 1 : -1));
	}
	return sortedData;
};

export {
	getAppendingOptions,
	getFilteredOptions,
	getMappedSelection,
	getPlaceholder,
	getPaperProps,
	getPopperProps,
	getOptionLabel,
	getSelectedOption,
	getSortedOptions,
	getTextTags,
	isAddingOption,
	isAllChecked,
	isAllDisabled,
	isAllSelectable,
	isAllSelected,
	isCompatibleOption,
	isHiddenInput,
};
